import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  Audience,
  BudgetState,
} from "../../../pages/Ads2DControlsAlphaBeta/shared";
import {
  Toast,
  getGrowthModeLabel,
} from "../../../pages/Ads2DControlsAlphaBeta/shared";

import { AdTypesModalProvider } from "./components/AdTypesExplainer";
import { AdvertisingSettingsPage } from "./components/AdvertisingSettingsPage";
import { BrandPortalSidebar } from "./components/BrandPortalSidebar";
import { BreakevenRoasProvider } from "./components/BreakevenRoas";
import { CardDensityProvider } from "./components/CardDensity";
import { ConfirmAdvertisingSettingsModal } from "./components/ConfirmAdvertisingSettingsModal";
import { CustomersTab } from "./components/CustomersTab";
import { ForecastModalProvider } from "./components/ForecastExplainer";
import { MonthDetailPageV2 } from "./components/month-detail-v2/MonthDetailPageV2";
import { OverviewTab } from "./components/OverviewTab";
import { ProductsTab } from "./components/ProductsTab";
import { PromotedListingsHeader } from "./components/PromotedListingsHeader";
import { PromotedListingsTabs } from "./components/PromotedListingsTabs";
import { StoreTypesModal } from "./components/StoreTypesModal";
import { ADS_MANAGER_SURFACE_CLASS } from "./constants/layout";
import {
  DAIRY_BRAND_PROFILE_TOKEN,
  DO_GOOD_CANDLE_PROFILE_TOKEN,
  ELANA_GABRIELLE_PROFILE_TOKEN,
  GOOD_BOOK_PROFILE_TOKEN,
  KAARI_CO_PROFILE_TOKEN,
  KING_FLOYDS_PROFILE_TOKEN,
  MALWEST_PROFILE_TOKEN,
  OGGI_PROFILE_TOKEN,
  P8_PROFILE_TOKEN,
  WOODSY_CRAFT_PROFILE_TOKEN,
  getP8DefaultBudget,
  getP8DefaultGrowthIdx,
  setActiveProfileToken,
} from "./p8AdvertiserData";
import type { PlTab, PrototypeVersion, SettingsTab, TopView } from "./types";

export const prototypeMeta: PrototypeMetadata = {
  name: "[ARCHIVED] Ads Manager 2.0",
  owner: "Irene Limb",
  email: "irene.limb@faire.com",
  status: "in-progress",
  createdAt: "2026-08-04",
  lastUpdated: "2026-09-16",
  surfaceArea: "Brand",
  category: "experimental",
  creationTool: "cursor",
  thumbnail: "/images/thumbnails/ads-manager-2.png",
  description:
    "Interview prototype for the revamped Ads Manager: redesigned Overview, per-month conversion drilldown, and a consolidated Customers tab, wired to real advertiser data.",
};

/** Advertiser-profile selector states (toolbar → active dataset). */
type ProfileState = 1 | 2 | 3 | 4 | 6 | 7 | 8 | 9 | 10 | 11;

const BREAKEVEN_ROAS_OFF = 1;
const BREAKEVEN_ROAS_ON = 2;

const PROFILE_TOKENS: Record<ProfileState, string> = {
  1: P8_PROFILE_TOKEN,
  2: ELANA_GABRIELLE_PROFILE_TOKEN,
  3: KAARI_CO_PROFILE_TOKEN,
  4: WOODSY_CRAFT_PROFILE_TOKEN,
  6: KING_FLOYDS_PROFILE_TOKEN,
  7: OGGI_PROFILE_TOKEN,
  8: GOOD_BOOK_PROFILE_TOKEN,
  9: MALWEST_PROFILE_TOKEN,
  10: DAIRY_BRAND_PROFILE_TOKEN,
  11: DO_GOOD_CANDLE_PROFILE_TOKEN,
};

type SavedSettings = {
  growthIdx: number;
  budget: BudgetState;
  audience: Audience;
  selectedStoreTypes: string[];
  newProductsBoosting: boolean;
};

function createDefaultSavedSettings(): SavedSettings {
  return {
    growthIdx: getP8DefaultGrowthIdx(),
    budget: getP8DefaultBudget(),
    audience: "both",
    selectedStoreTypes: [],
    newProductsBoosting: false,
  };
}

function settingsAreDirty(
  current: SavedSettings,
  saved: SavedSettings
): boolean {
  return (
    current.growthIdx !== saved.growthIdx ||
    current.audience !== saved.audience ||
    current.newProductsBoosting !== saved.newProductsBoosting ||
    current.budget.separateBudget !== saved.budget.separateBudget ||
    current.budget.overallBudget !== saved.budget.overallBudget ||
    current.budget.newCustomerBudget !== saved.budget.newCustomerBudget ||
    current.budget.returningBudget !== saved.budget.returningBudget ||
    current.selectedStoreTypes.join("\0") !==
      saved.selectedStoreTypes.join("\0")
  );
}

const AdsManager2Page: React.FC = () => {
  const [version, setVersion] = useState<PrototypeVersion>(3);
  const [topView, setTopView] = useState<TopView>("promoted-listings");
  const [plTab, setPlTab] = useState<PlTab>("overview");
  const [settingsTab, setSettingsTab] = useState<SettingsTab>("core");
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number | null>(
    null
  );
  const [selectedStoreTypes, setSelectedStoreTypes] = useState<string[]>([]);
  const [newProductsBoosting, setNewProductsBoosting] = useState(false);
  const [breakevenRoasState, setBreakevenRoasState] =
    useState(BREAKEVEN_ROAS_OFF);
  // Dairy Brand. Must stay in step with the module-level default in
  // p8AdvertiserData.ts: the budget and growth useState initializers below read
  // the active token during this same first render, so if the two disagree the
  // page opens showing one brand's numbers under another brand's settings.
  const [profileState, setProfileState] = useState<ProfileState>(10);
  const budgetConstrained = false;
  const [modalOpen, setModalOpen] = useState(false);
  const [growthIdx, setGrowthIdx] = useState(getP8DefaultGrowthIdx());
  const [budget, setBudget] = useState<BudgetState>(getP8DefaultBudget());
  const [audience, setAudience] = useState<Audience>("both");
  const [savedSettings, setSavedSettings] = useState<SavedSettings>(() =>
    createDefaultSavedSettings()
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("Settings saved");
  const [showToast, setShowToast] = useState(false);

  const showToastWithMessage = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    window.setTimeout(() => setShowToast(false), 4000);
  }, []);

  const currentSettings = useMemo<SavedSettings>(
    () => ({
      growthIdx,
      budget,
      audience,
      selectedStoreTypes,
      newProductsBoosting,
    }),
    [growthIdx, budget, audience, selectedStoreTypes, newProductsBoosting]
  );

  const isDirty = settingsAreDirty(currentSettings, savedSettings);

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  const handleProfileChange = useCallback((state: ProfileState) => {
    // Set the module-level active token synchronously BEFORE the state update so
    // the re-render reads the new profile. (Do not mirror this in an effect keyed
    // on profileState — a stale-closure effect would clobber the token post-render.)
    setActiveProfileToken(PROFILE_TOKENS[state]);
    const defaultBudget = getP8DefaultBudget();
    const defaultGrowthIdx = getP8DefaultGrowthIdx();
    setProfileState(state);
    setBudget(defaultBudget);
    setGrowthIdx(defaultGrowthIdx);
    setSavedSettings((prev) => ({
      ...prev,
      budget: defaultBudget,
      growthIdx: defaultGrowthIdx,
    }));
  }, []);

  const openSettings = (
    tab: SettingsTab = "core",
    options?: { openStoreTypesModal?: boolean }
  ) => {
    setTopView("advertising-settings");
    setSettingsTab(tab);
    if (options?.openStoreTypesModal) {
      setModalOpen(true);
    }
  };

  const openMonthDetail = useCallback((monthIndex: number) => {
    setSelectedMonthIndex(monthIndex);
    setTopView("month-detail");
    window.scrollTo({ top: 0 });
  }, []);

  const handleStoreTypesConfirm = (types: string[]) => {
    setSelectedStoreTypes(types);
    setModalOpen(false);
  };

  const handleUndoSettings = useCallback(() => {
    setGrowthIdx(savedSettings.growthIdx);
    setBudget(savedSettings.budget);
    setAudience(savedSettings.audience);
    setSelectedStoreTypes(savedSettings.selectedStoreTypes);
    setNewProductsBoosting(savedSettings.newProductsBoosting);
  }, [savedSettings]);

  const handleConfirmSettings = useCallback(() => {
    setSavedSettings(currentSettings);
    setConfirmModalOpen(false);
    showToastWithMessage("Settings saved");
  }, [currentSettings, showToastWithMessage]);

  return (
    <>
      <LiveToolbar
        variantGroups={[
          {
            title: "Version",
            queryKey: "version",
            variants: [
              { variantNumber: 1, label: "V1" },
              { variantNumber: 2, label: "V2" },
              { variantNumber: 3, label: "V3" },
            ],
            active: version,
            onChange: (nextVersion) =>
              setVersion(nextVersion as PrototypeVersion),
          },
          ...(version === 3
            ? [
                {
                  title: "Breakeven ROAS",
                  queryKey: "breakeven-roas",
                  toggleMode: true,
                  variants: [
                    { variantNumber: BREAKEVEN_ROAS_OFF, label: "Off" },
                    { variantNumber: BREAKEVEN_ROAS_ON, label: "On" },
                  ],
                  active: breakevenRoasState,
                  onChange: setBreakevenRoasState,
                },
              ]
            : []),
        ]}
        stateGroups={[
          {
            title: "Advertiser profile",
            queryKey: "profile",
            description:
              "The advertiser dataset backing the prototype — an anonymized archetype or a real brand's latest data.",
            // Only the brands worth showing are listed. The gaps in the state
            // numbers are deliberate: `queryKey` puts the number in the URL, so
            // renumbering what's left would silently repoint links someone has
            // already shared. Other unlisted numbers still resolve in
            // PROFILE_TOKENS even though they aren't offered in the panel.
            states: [
              { stateNumber: 10, label: "Dairy Brand" },
              { stateNumber: 2, label: "Textile Brand" },
              { stateNumber: 11, label: "Do Good Candle Studio" },
            ],
            active: profileState,
            onChange: (state) => handleProfileChange(state as ProfileState),
            dividerAfter: true,
          },
        ]}
      />

      {/* Both wrap the whole surface because both explainers are reachable from
          more than one place: the forecast one from the Overview's month table,
          its ROAS legend, and the cohort view; the ad types one from the ad
          type filter's info button and from the Total ad sales tooltip. One
          dialog each between all of those, rather than one per trigger — and a
          dialog opened from inside a tooltip panel cannot be owned by it, since
          the panel unmounts as soon as the pointer leaves. */}
      <ForecastModalProvider>
        <AdTypesModalProvider>
          {/* V3's density pass. Wraps the whole surface so table rows, charts,
              and copy tighten together; card padding and the gutter between
              cards stay at 24px. */}
          <CardDensityProvider
            density={version === 3 ? "compact" : "comfortable"}
          >
            <BreakevenRoasProvider
              enabled={
                version === 3 && breakevenRoasState === BREAKEVEN_ROAS_ON
              }
              ratio={profileState === 10 ? 4.1 : 2.1}
            >
              <div
                className={`flex h-dvh min-h-0 overflow-hidden text-[#333] ${ADS_MANAGER_SURFACE_CLASS}`}
              >
                <BrandPortalSidebar />
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  {topView === "advertising-settings" ? (
                    <AdvertisingSettingsPage
                      settingsTab={settingsTab}
                      onSettingsTabChange={setSettingsTab}
                      onBack={() => setTopView("promoted-listings")}
                      growthIdx={growthIdx}
                      onGrowthIdxChange={setGrowthIdx}
                      budget={budget}
                      onBudgetChange={(next) =>
                        setBudget((prev) => ({ ...prev, ...next }))
                      }
                      audience={audience}
                      onAudienceChange={setAudience}
                      selectedStoreTypes={selectedStoreTypes}
                      newProductsBoosting={newProductsBoosting}
                      budgetConstrained={budgetConstrained}
                      onSelectStoreTypes={() => setModalOpen(true)}
                      onNewProductsBoostingChange={setNewProductsBoosting}
                      isDirty={isDirty}
                      onSave={() => setConfirmModalOpen(true)}
                      onUndo={handleUndoSettings}
                    />
                  ) : topView === "month-detail" &&
                    selectedMonthIndex != null ? (
                    <MonthDetailPageV2
                      monthIndex={selectedMonthIndex}
                      isV3={version === 3}
                      onBack={() => {
                        setTopView("promoted-listings");
                        setPlTab("overview");
                      }}
                    />
                  ) : (
                    <>
                      <PromotedListingsHeader
                        onOpenSettings={() => openSettings()}
                      />
                      <PromotedListingsTabs
                        activeTab={plTab}
                        onTabChange={setPlTab}
                      />
                      <div
                        className={`min-h-0 flex-1 overflow-y-auto ${ADS_MANAGER_SURFACE_CLASS}`}
                      >
                        {plTab === "overview" ? (
                          <OverviewTab
                            version={version}
                            onOpenMonth={openMonthDetail}
                          />
                        ) : null}
                        {plTab === "customers" ? (
                          <CustomersTab
                            useTotalAdSales={version === 3}
                            defaultChartMetric={
                              version === 3 ? "impressions" : "ad-sales"
                            }
                            customerValueAboveChart={version === 3}
                          />
                        ) : null}
                        {plTab === "products" ? (
                          <ProductsTab
                            newProductsBoosting={newProductsBoosting}
                          />
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </BreakevenRoasProvider>
          </CardDensityProvider>
        </AdTypesModalProvider>
      </ForecastModalProvider>

      <StoreTypesModal
        open={modalOpen}
        selected={selectedStoreTypes}
        onClose={() => setModalOpen(false)}
        onConfirm={handleStoreTypesConfirm}
        onSuggestionSubmitted={() =>
          showToastWithMessage("Suggestion submitted")
        }
      />

      <ConfirmAdvertisingSettingsModal
        open={confirmModalOpen}
        growthModeLabel={getGrowthModeLabel("A", growthIdx)}
        budget={budget}
        audience={audience}
        selectedStoreTypes={selectedStoreTypes}
        newProductsBoosting={newProductsBoosting}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmSettings}
      />

      <Toast message={toastMessage} visible={showToast} />
    </>
  );
};

export default AdsManager2Page;
